import {
  ChangeEventHandler,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Space, Table } from 'antd';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';
import { useUIDispatchContext } from 'providers/UI';
import { useCheckOrganization } from 'hooks';
import { deleteService, fetchServices } from 'services/api/services';
import { ServiceListRow } from 'types/api/services';

import useOrgEffect from 'hooks/useOrgEffect';
import { OrganizationAlert, Private } from 'components/shared';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';

const { Search } = Input;

const PAGE_LIMIT = 10;

const Services: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { toggleModal } = useUIDispatchContext();
  const { checkOrganizationWrapper } = useCheckOrganization();
  const timer = useRef<NodeJS.Timeout | null>(null);

  const [results, setResults] = useState<ServiceListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchServices({
          page,
          limit: PAGE_LIMIT,
          name: search || undefined
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, search, refreshKey]);

  const onDeleteService = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        onConfirm: async () => {
          try {
            await deleteService(id);
            setRefreshKey(k => k + 1);
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [toggleModal]
  );

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    []
  );

  const columns: ColumnProps<ServiceListRow>[] = useMemo(
    () => [
      {
        title: t('common.id'),
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('service.title'),
        dataIndex: 'title',
        key: 'title'
      },
      {
        title: t('service.short_desc'),
        dataIndex: 'short_desc',
        key: 'short_desc'
      },
      {
        title: t('service.organization'),
        dataIndex: 'organization',
        key: 'organization',
        render: (_, item) => item.organization?.name
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: (id: number) => (
          <ActionMenu
            data={[
              {
                title: t('table.edit'),
                permission: {
                  route: PERMISSION_ROUTES.blog,
                  action: PERMISSION_ACTIONS.update
                },
                action() {
                  checkOrganizationWrapper(() =>
                    navigate(`/services/details/${id}`)
                  );
                }
              },
              {
                title: t('table.delete'),
                permission: {
                  route: PERMISSION_ROUTES.services,
                  action: PERMISSION_ACTIONS.update
                },
                menuProps: {
                  style: {
                    color: 'var(--danger-color)'
                  }
                },
                action: () => onDeleteService(+id)
              }
            ]}
            placement="bottomRight"
            icon={<EllipsisOutlined />}
          />
        )
      }
    ],
    [checkOrganizationWrapper, navigate, onDeleteService, t]
  );

  return (
    <PageWrapper
      title={t('service.page_title')}
      color="white"
      extra={[
        <Private
          key="add-service-button"
          route={PERMISSION_ROUTES.services}
          action={PERMISSION_ACTIONS.create}
        >
          <OrganizationAlert>
            <Button
              type="primary"
              onClick={() => navigate('/services/details')}
            >
              <PlusOutlined />
              {t('service.add_new')}
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Search
                placeholder={t('common.search')}
                onChange={onSearchChange}
              />
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default Services;
